import { Alert, NumberInput, SimpleGrid, Stack, Text } from '@mantine/core';
import { schemaResolver, useForm } from '@mantine/form';
import { IconClockExclamation, IconCoins, IconInfoCircle } from '@tabler/icons-react';
import { Panel } from 'components/Panel';
import { SaveButton } from 'components/SaveButton';
import { useCompanyConfiguration } from 'init/use-company-configuration';
import { i18nKeys, useTranslation } from 'locales';
import { useEffect } from 'react';
import { pick } from 'remeda';
import { currencySymbol } from 'shared/utils/currency';
import { z } from 'zod/v4';

import { type CardOption, OptionCardGroup } from '../../components';
import { RecoverySettingsSchema, useLoadRecoverySettings, useUpdateRecoverySettings } from '../api';

const ReminderThresholdsSchema = RecoverySettingsSchema.pick({
  minimumSaldoSending: true,
  reminderThresholdType: true,
  thresholdAmount: true,
});

const ReminderThresholdsFormSchema = ReminderThresholdsSchema.extend({
  minimumSaldoSending: z.number().min(0).nullable(),
  thresholdAmount: z.number().min(0).nullable(),
});

type ReminderThresholds = z.infer<typeof ReminderThresholdsSchema>;
type ReminderThresholdsForm = z.infer<typeof ReminderThresholdsFormSchema>;
type AmountField = 'minimumSaldoSending' | 'thresholdAmount';

const REMINDER_THRESHOLDS_FIELDS = ReminderThresholdsSchema.keyof().options;

const INITIAL_VALUES: ReminderThresholdsForm = {
  minimumSaldoSending: 0,
  reminderThresholdType: 'remaining_balance',
  thresholdAmount: 0,
};

const toThresholds = (values: ReminderThresholdsForm): ReminderThresholds => ({
  ...values,
  minimumSaldoSending: values.minimumSaldoSending ?? 0,
  thresholdAmount: values.thresholdAmount ?? 0,
});

const THRESHOLD_TYPE_OPTIONS: CardOption<ReminderThresholds['reminderThresholdType']>[] = [
  {
    value: 'remaining_balance',
    labelKey: i18nKeys.FORM.PREFERENCES.REMAINING_BALANCE,
    Icon: IconCoins,
  },
  {
    value: 'overdue_balance',
    labelKey: i18nKeys.FORM.PREFERENCES.OVERDUE_BALANCE,
    Icon: IconClockExclamation,
  },
];

export const ReminderThresholdsCard = () => {
  const { t } = useTranslation();
  const company = useCompanyConfiguration();
  const currencySection = (
    <Text size="sm" c="dimmed">
      {currencySymbol(undefined, company)}
    </Text>
  );

  const { recoverySettings } = useLoadRecoverySettings();
  const { updateRecoverySettings, isUpdateRecoverySettingsLoading } = useUpdateRecoverySettings();

  const form = useForm<ReminderThresholdsForm>({
    initialValues: INITIAL_VALUES,
    validate: schemaResolver(ReminderThresholdsFormSchema, { sync: true }),
  });

  const amountInputProps = (field: AmountField) => ({
    ...form.getInputProps(field),
    value: form.getValues()[field] ?? '',
    onChange: (next: number | string) =>
      form.setFieldValue(field, typeof next === 'number' ? next : null),
    onBlur: () => {
      if (form.getValues()[field] === null) form.setFieldValue(field, 0);
    },
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: resync only when this card's own fields change on the server, so saving the sibling recovery settings card does not wipe unsaved edits here
  useEffect(() => {
    if (!recoverySettings) return;

    const thresholds = ReminderThresholdsSchema.parse(recoverySettings);
    form.setInitialValues(thresholds);
    form.setValues(thresholds);
  }, [
    recoverySettings?.minimumSaldoSending,
    recoverySettings?.reminderThresholdType,
    recoverySettings?.thresholdAmount,
  ]);

  const handleSave = () => {
    if (form.validate().hasErrors) return;

    const dirtyFields = REMINDER_THRESHOLDS_FIELDS.filter(field => form.isDirty(field));

    updateRecoverySettings(pick(toThresholds(form.getValues()), dirtyFields), {
      onSuccess: () => form.resetDirty(),
    });
  };

  const { reminderThresholdType } = form.getValues();

  return (
    <Panel
      title={t(i18nKeys.FORM.PREFERENCES.REMINDER_THRESHOLDS)}
      withBorder
      footer={
        <SaveButton
          onClick={handleSave}
          disabled={!form.isDirty()}
          loading={isUpdateRecoverySettingsLoading}
          hideIcon
        />
      }
    >
      <Stack my="md" gap="lg">
        <Alert variant="light" icon={<IconInfoCircle size={18} />}>
          {t(i18nKeys.FORM.PREFERENCES.REMINDER_THRESHOLDS_DESCRIPTION)}
        </Alert>
        <SimpleGrid cols={{ base: 1, md: 2 }} spacing="xl" verticalSpacing="lg">
          <Stack gap="lg">
            <NumberInput
              label={t(i18nKeys.FORM.PREFERENCES.BALANCE_THRESHOLD)}
              min={0}
              allowNegative={false}
              decimalScale={2}
              hideControls
              rightSection={currencySection}
              rightSectionPointerEvents="none"
              {...amountInputProps('minimumSaldoSending')}
            />
            <NumberInput
              label={t(i18nKeys.FORM.PREFERENCES.INVOICE_THRESHOLD)}
              min={0}
              allowNegative={false}
              decimalScale={2}
              hideControls
              rightSection={currencySection}
              rightSectionPointerEvents="none"
              {...amountInputProps('thresholdAmount')}
            />
          </Stack>
          <OptionCardGroup
            label={t(i18nKeys.FORM.PREFERENCES.BALANCE_THRESHOLD_TYPE)}
            value={reminderThresholdType}
            options={THRESHOLD_TYPE_OPTIONS}
            onChange={next => form.setFieldValue('reminderThresholdType', next)}
          />
        </SimpleGrid>
      </Stack>
    </Panel>
  );
};
